import { t } from "../i18n";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export type PetPhotoVariant = "hero" | "avatar" | "view-hero" | "tomb" | "upload";

export type PetPalette = {
  tint: string;
  tint2: string;
  icon: string;
};

export const PET_PALETTES: PetPalette[] = [
  { tint: "#EEE3CF", tint2: "#F5ECDC", icon: "#D2BE92" },
  { tint: "#E8DDD2", tint2: "#F1E8DE", icon: "#C7B49E" },
  { tint: "#E7E0F2", tint2: "#EFEAF8", icon: "#B6ADD0" },
  { tint: "#E3E7DC", tint2: "#ECEFE6", icon: "#A9B7A2" },
  { tint: "#EADFEF", tint2: "#F3ECF6", icon: "#BCA4C8" },
  { tint: "#F0DEDC", tint2: "#F8ECEB", icon: "#D2A6A1" },
  { tint: "#E2E6EC", tint2: "#EEF1F5", icon: "#A7B2C0" },
];

export const DEFAULT_PALETTE: PetPalette = PET_PALETTES[2];

function hashSeed(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return h;
}

export function getPetPalette(seed?: string | null): PetPalette {
  if (!seed) return DEFAULT_PALETTE;
  return PET_PALETTES[hashSeed(seed) % PET_PALETTES.length];
}

function stripeGradient(tint: string, tint2: string, stripe: number, gap: number): string {
  return `repeating-linear-gradient(135deg,${tint},${tint} ${stripe}px,${tint2} ${stripe}px,${tint2} ${gap}px)`;
}

export type PetPhotoDeco = {
  palette: PetPalette;
  avatarBackground: string;
  heroBackground: string;
  pawOpacity: number;
  heroLabel: string;
  hasPhoto: boolean;
};

export function petPhotoDeco(opts: {
  photoUrl?: string | null;
  seed?: string | null;
  name?: string | null;
}): PetPhotoDeco {
  const palette = getPetPalette(opts.seed);
  const photo = opts.photoUrl?.trim() || "";
  const hasPhoto = !!photo;

  const avatarBackground = hasPhoto
    ? `center/cover no-repeat url("${photo}")`
    : stripeGradient(palette.tint, palette.tint2, 6, 12);

  const heroBackground = hasPhoto
    ? `center/cover no-repeat url("${photo}")`
    : stripeGradient(palette.tint, palette.tint2, 9, 18);

  const heroLabel =
    hasPhoto || !opts.name?.trim()
      ? ""
      : t("petPhoto.heroLabel", { name: opts.name.trim() });

  return {
    palette,
    avatarBackground,
    heroBackground,
    pawOpacity: hasPhoto ? 0 : 1,
    heroLabel,
    hasPhoto,
  };
}

export function pawIconSvg(size: number, color: string, opacity = 1): string {
  const style = opacity < 1 ? ` style="opacity:${opacity}"` : "";
  return `<svg class="pet-photo__paw" width="${size}" height="${size}" viewBox="0 0 24 24" fill="${color}" aria-hidden="true"${style}><ellipse cx="7" cy="11" rx="2" ry="2.6"></ellipse><ellipse cx="12" cy="9.2" rx="2" ry="2.8"></ellipse><ellipse cx="17" cy="11" rx="2" ry="2.6"></ellipse><path d="M12 13c-2.5 0-4.4 1.7-4.4 3.6 0 1.4 1.2 2.1 4.4 2.1s4.4-.7 4.4-2.1C16.4 14.7 14.5 13 12 13z"></path></svg>`;
}

function uploadIconSvg(size: number, color: string): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><path d="M12 3v13M7 8l5-5 5 5"></path></svg>`;
}

const PAW_SIZE: Record<PetPhotoVariant, number> = {
  hero: 32,
  avatar: 26,
  "view-hero": 32,
  tomb: 22,
  upload: 24,
};

export type RenderPetPhotoOpts = {
  variant: PetPhotoVariant;
  photoUrl?: string | null;
  seed?: string | null;
  name?: string | null;
  className?: string;
  alt?: string;
};

function variantClasses(variant: PetPhotoVariant, extra?: string): string {
  const base = ["pet-photo", `pet-photo--${variant}`];
  if (extra) base.push(extra);
  return base.join(" ");
}

function buildPhotoInnerHtml(opts: RenderPetPhotoOpts, deco: PetPhotoDeco): string {
  const pawSize = PAW_SIZE[opts.variant];
  const paw = pawIconSvg(pawSize, deco.palette.icon, deco.pawOpacity);
  const label = opts.variant === "view-hero" && deco.heroLabel
    ? `<span class="pet-photo__hero-label">${escapeHtml(deco.heroLabel)}</span>`
    : "";
  return `${paw}${label}`;
}

function backgroundForVariant(variant: PetPhotoVariant, deco: PetPhotoDeco): string {
  if (variant === "hero" || variant === "view-hero") return deco.heroBackground;
  return deco.avatarBackground;
}

export function renderPetPhotoHtml(opts: RenderPetPhotoOpts): string {
  const deco = petPhotoDeco(opts);
  const ariaLabel = deco.hasPhoto
    ? opts.alt ?? opts.name ?? ""
    : t("posts.noPhoto");

  if (deco.hasPhoto) {
    const cls = opts.className ?? variantClasses(opts.variant);
    const seedAttr = opts.seed ? ` data-pet-photo-seed="${escapeHtml(opts.seed)}"` : "";
    const nameAttr = opts.name ? ` data-pet-photo-name="${escapeHtml(opts.name)}"` : "";
    return `<img class="${cls}" src="${escapeHtml(opts.photoUrl!)}" alt="${escapeHtml(ariaLabel)}" loading="lazy" decoding="async" data-pet-photo-variant="${opts.variant}"${seedAttr}${nameAttr}>`;
  }

  const cls = opts.className ?? variantClasses(opts.variant, "pet-photo--placeholder");
  const bg = backgroundForVariant(opts.variant, deco);
  return `<div class="${cls}" style="background:${bg}" role="img" aria-label="${escapeHtml(ariaLabel)}">${buildPhotoInnerHtml(opts, deco)}</div>`;
}

export function renderPetPhotoElement(opts: RenderPetPhotoOpts): HTMLElement {
  const deco = petPhotoDeco(opts);
  const ariaLabel = deco.hasPhoto
    ? opts.alt ?? opts.name ?? ""
    : t("posts.noPhoto");

  if (deco.hasPhoto) {
    const img = document.createElement("img");
    img.className = opts.className ?? variantClasses(opts.variant);
    img.src = opts.photoUrl!;
    img.alt = ariaLabel;
    img.loading = "lazy";
    img.decoding = "async";
    bindPetPhotoFallback(img, opts);
    return img;
  }

  const el = document.createElement("div");
  el.className = opts.className ?? variantClasses(opts.variant, "pet-photo--placeholder");
  el.style.background = backgroundForVariant(opts.variant, deco);
  el.setAttribute("role", "img");
  el.setAttribute("aria-label", ariaLabel);
  el.innerHTML = buildPhotoInnerHtml(opts, deco);
  return el;
}

export function bindPetPhotoFallback(
  img: HTMLImageElement,
  opts: Omit<RenderPetPhotoOpts, "photoUrl">,
): void {
  img.dataset.petPhotoVariant = opts.variant;
  if (opts.seed) img.dataset.petPhotoSeed = opts.seed;
  if (opts.name) img.dataset.petPhotoName = opts.name;
  img.onerror = () => {
    const placeholder = renderPetPhotoElement({ ...opts, photoUrl: null });
    img.replaceWith(placeholder);
  };
}

export function attachPetPhotoFallbacks(root: ParentNode = document): void {
  root.querySelectorAll<HTMLImageElement>("img[data-pet-photo-variant]").forEach((img) => {
    if (img.dataset.petPhotoBound) return;
    img.dataset.petPhotoBound = "1";
    bindPetPhotoFallback(img, {
      variant: img.dataset.petPhotoVariant as PetPhotoVariant,
      seed: img.dataset.petPhotoSeed,
      name: img.dataset.petPhotoName,
      className: img.className,
      alt: img.alt,
    });
  });
}

export function mountPetPhoto(
  container: HTMLElement,
  opts: RenderPetPhotoOpts,
): void {
  container.replaceChildren(renderPetPhotoElement(opts));
}

export function renderUploadEmptyHtml(): string {
  const title = escapeHtml(t("petPhoto.uploadTitle"));
  const hint = escapeHtml(t("petPhoto.uploadHint"));
  const ctaBefore = escapeHtml(t("petPhoto.uploadCtaBefore"));
  const ctaLink = escapeHtml(t("petPhoto.uploadCtaLink"));

  return `
    <div class="pet-photo-upload">
      <div class="pet-photo-upload__empty pet-photo-upload__empty--mobile">
        <div class="pet-photo-upload__icon pet-photo-upload__icon--mobile">
          ${uploadIconSvg(24, "#C6A24C")}
        </div>
        <div class="pet-photo-upload__copy">
          <div class="pet-photo-upload__title">${title}</div>
          <div class="pet-photo-upload__hint">${hint}</div>
        </div>
      </div>
      <div class="pet-photo-upload__empty pet-photo-upload__empty--desktop">
        ${uploadIconSvg(28, "#B0A8C4")}
        <span class="pet-photo-upload__cta">${ctaBefore}<span class="pet-photo-upload__cta-link">${ctaLink}</span></span>
      </div>
      <div class="pet-photo-upload__preview" hidden></div>
    </div>
  `;
}

export function updateCreaPhotoUpload(container: HTMLElement, file: File | null, seed?: string): void {
  const preview = container.querySelector(".pet-photo-upload__preview") as HTMLElement | null;
  const emptyMobile = container.querySelector(".pet-photo-upload__empty--mobile") as HTMLElement | null;
  const emptyDesktop = container.querySelector(".pet-photo-upload__empty--desktop") as HTMLElement | null;

  if (file && preview) {
    const url = URL.createObjectURL(file);
    preview.innerHTML = `<img src="${url}" alt="">`;
    preview.hidden = false;
    if (emptyMobile) emptyMobile.hidden = true;
    if (emptyDesktop) emptyDesktop.hidden = true;
    container.classList.add("pet-photo-upload--has-photo");
    return;
  }

  if (preview) {
    preview.innerHTML = "";
    preview.hidden = true;
  }
  if (emptyMobile) emptyMobile.hidden = false;
  if (emptyDesktop) emptyDesktop.hidden = false;
  container.classList.remove("pet-photo-upload--has-photo");
  void seed;
}

export function renderCreaPreviewPhoto(container: HTMLElement, file: File | null, seed?: string): void {
  if (file) {
    const url = URL.createObjectURL(file);
    container.replaceChildren();
    const img = document.createElement("img");
    img.src = url;
    img.alt = "";
    container.appendChild(img);
    return;
  }

  mountPetPhoto(container, {
    variant: "avatar",
    photoUrl: null,
    seed: seed ?? "crea-preview",
  });
}
